import React, { Suspense, lazy, memo, useEffect } from "react";
import { useSelector } from "react-redux";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { isEmpty } from "../helpers/Functions";
import LoginInit from "./LoginInit";
import { setAppVersion } from "./redux/slices/appSlice";

const Layout = lazy(() => import("../theme/layout/Layout"));
const SplashScreen = lazy(() => import("./SplashScreen"));
const QuickAccessPage = lazy(() => import("./QuickAccessPage"));

const Login = lazy(() => import("./Login"));
const ErrorPage = lazy(() => import("./ErrorPage"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const LibraryAccessApproval = lazy(() => import("./LibraryAccessApproval"));
const DashboardRoutes = lazy(() => import("./dashboard/DashboardRoutes"));
const AdminPanelRoutes = lazy(() => import("./admin-panel/AdminPanelRoutes"));
const CertificatePage = lazy(() => import("./CertificatePage"));
const WaitingArea = lazy(() => import("./WaitingArea"));
const ZoomRoom = lazy(() => import("./ZoomRoom"));
const HostArea = lazy(() => import("./HostArea"));
const ReviewPage = lazy(() => import("./ReviewPage"));
const EventPortalPage = lazy(() => import("./EventPortalPage"));
const GPTLogin = lazy(() => import("./GPTLogin"));
const CustomEmailApproval = lazy(() => import("./CustomEmailApproval"));
const CourseReferralPage = lazy(() => import("./CourseReferralPage"));
const LiveLessonRoom = lazy(() => import("./LiveLessonsRoom"));
const QuickRegisterPage = lazy(() => import("./QuickRegisterPage"));

export const AppRoutes = memo(() => {
	const isAuthorized = useSelector(({ auth }) => !isEmpty(auth.token));
	const isSuperAdmin = useSelector(({ auth }) => auth.is_super_admin === 1);
	const appVersion = useSelector(({ app }) => app.app_version);
	const navigate = useNavigate();

	useEffect(() => {
		if (process.env.REACT_APP_VERSION !== appVersion && window.location.pathname !== "/login") {
			localStorage.clear();
			setAppVersion(process.env.REACT_APP_VERSION);

			if (window.location.pathname === "/dashboard" || window.location.pathname === "/admin-panel") {
				navigate("/login");
				window.location.reload();
			}

			return false;
		}
	}, []);

	return (
		<Suspense fallback={<SplashScreen />}>
			<Routes>
				<Route path="/" element={<Navigate to="/dashboard/ai-chat" />} />
				<Route path="/error" element={<ErrorPage />} />
				<Route path="/login" element={<Login />} />
				<Route path="/gpt-login/:library_code" element={<GPTLogin />} />
				<Route path="/reset-password" element={<ResetPassword />} />
				<Route path="/reset-password/:email/:email_token" element={<ResetPassword />} />
				<Route
					path="/approve-library-access-from-email/:account_party_id/:library_party_id/:credit_request"
					element={<LibraryAccessApproval />}
				/>
				<Route path="/me" element={<QuickAccessPage />} />
				<Route path="/feedback" element={<ReviewPage company={"olern"} />} />
				<Route path="/tarkie" element={<ReviewPage company={"tarkie"} />} />
				<Route path="/events/:library_code/:access_code" element={<WaitingArea />} />
				<Route path="/events/" element={<EventPortalPage />} />
				<Route path="/me/:access_code" element={<QuickAccessPage />} />
				<Route path="/certificate/:user_certificate_id/" element={<CertificatePage />} />
				<Route path="/zoom_room/:role" element={<ZoomRoom />} />
				<Route path="/custom_email/:custom_email_id" element={<CustomEmailApproval />} />
				<Route path="/events/host_room/:tab/:library_code" element={<HostArea />} />
				<Route path="/referral/:library_code" element={<CourseReferralPage />} />
				<Route path="/live-lesson/:library_code/:email/" element={<LiveLessonRoom />} />
				<Route path="/quick-register/:library_code/" element={<QuickRegisterPage />} />

				{!isAuthorized &&
					["dashboard/*", "admin-panel/*"].map((path, i) => (
						<Route key={i} path={path} element={<Navigate to="/login" />} />
					))
				}

				<Route path="/init" element={<LoginInit />} />

				<Route element={<Layout />}>
					<Route path="/dashboard/*" element={<DashboardRoutes />} />
					{isSuperAdmin && <Route path="/admin-panel/*" element={<AdminPanelRoutes />} />}
				</Route>

				<Route path="*" element={<Navigate to="/error" />} />
			</Routes>
		</Suspense>
	);
});
